import React, { useState } from 'react';
import axios from 'axios';
import { formatDateForDisplay, getCurrentDateForInput } from '../utils/dateFormat';
import { useLanguage } from '../contexts/LanguageContext';

const PartialPaymentModal = ({ invoice, onClose, onPaymentAdded }) => {
  const { t } = useLanguage();
  const [paymentData, setPaymentData] = useState({
    paymentDate: getCurrentDateForInput(),
    paymentMode: 'CASH',
    amountPaid: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`/api/invoices/${invoice.id}/payments`, {
        ...paymentData,
        amountPaid: parseFloat(paymentData.amountPaid)
      });

      onPaymentAdded(response.data);
      onClose();
    } catch (error) {
      console.error('Failed to add payment:', error);
      setError(error.response?.data?.message || t('Failed to add payment. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const remainingAmount = invoice.totalAmount - (invoice.totalPaidAmount || invoice.paidAmount || 0);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'var(--white)',
        borderRadius: '12px',
        padding: '30px',
        width: '90%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
          {t('Add Partial Payment')}
        </h3>

        <div style={{
          background: 'var(--pale-blue)',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <h4 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
            {t('Invoice Details')}
          </h4>
          <div style={{ fontSize: '14px' }}>
            <div><strong>{t('Invoice #')}:</strong> {invoice.invoiceNumber}</div>
            <div><strong>{t('Total Amount')}:</strong> ₹{invoice.totalAmount}</div>
            <div><strong>{t('Already Paid')}:</strong> ₹{invoice.totalPaidAmount || invoice.paidAmount || 0}</div>
            <div><strong>{t('Remaining')}:</strong> ₹{remainingAmount.toFixed(2)}</div>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="paymentDate" className="form-label">
              {t('Payment Date')} *
            </label>
            <input
              type="date"
              id="paymentDate"
              className="form-input"
              value={paymentData.paymentDate}
              onChange={(e) => setPaymentData({...paymentData, paymentDate: e.target.value})}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="paymentMode" className="form-label">
              {t('Payment Mode')} *
            </label>
            <select
              id="paymentMode"
              className="form-input"
              value={paymentData.paymentMode}
              onChange={(e) => setPaymentData({...paymentData, paymentMode: e.target.value})}
              required
              disabled={loading}
            >
              <option value="CASH">Cash</option>
              <option value="CARD">Card</option>
              <option value="UPI">UPI</option>
              <option value="BANK_TRANSFER">Bank Transfer</option>
              <option value="CHEQUE">Cheque</option>
              <option value="NET_BANKING">Net Banking</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="amountPaid" className="form-label">
              {t('Amount Paid')} (₹) *
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={remainingAmount}
              id="amountPaid"
              className="form-input"
              value={paymentData.amountPaid}
              onChange={(e) => setPaymentData({...paymentData, amountPaid: e.target.value})}
              required
              disabled={loading}
              placeholder={t('Max: ₹{amount}', { amount: remainingAmount.toFixed(2) })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              {t('Notes')}
            </label>
            <textarea
              id="notes"
              className="form-input"
              rows="3"
              value={paymentData.notes}
              onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
              disabled={loading}
              placeholder={t('Payment reference, transaction ID, etc.')}
            />
          </div>

          <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              {t('Cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !paymentData.amountPaid || parseFloat(paymentData.amountPaid) <= 0}
            >
              {loading ? t('Adding...') : t('Add Payment')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PartialPaymentModal;
